import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Statistic, Button, Space, Tag, Table, message, Spin, Tabs } from 'antd';
import { ArrowLeftOutlined, PauseCircleOutlined, MailOutlined, EyeOutlined, LinkOutlined, CheckCircleOutlined, StopOutlined } from '@ant-design/icons';
import { campaignsApi } from '../services/api';
import dayjs from 'dayjs';

const API_BASE = '/api/v1';

const CampaignDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [recipients, setRecipients] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // 이메일 미리보기
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewSubject, setPreviewSubject] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [id]);

  const fetchData = async () => {
    try {
      const [campaignRes, recipientsRes] = await Promise.all([
        campaignsApi.get(id),
        campaignsApi.getRecipients(id)
      ]);
      setCampaign(campaignRes.data);
      setRecipients(recipientsRes.data);
    } catch (error) {
      message.error('캠페인 정보를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 이메일 미리보기 로드
  const loadPreview = async () => {
    if (previewHtml) return; // 이미 로드됨
    setPreviewLoading(true);
    try {
      const res = await fetch(`${API_BASE}/campaigns/${id}/preview`);
      if (res.ok) {
        const data = await res.json();
        setPreviewHtml(data.html || null);
        setPreviewSubject(data.subject || '');
      } else {
        setPreviewHtml('<p style="color:#999;text-align:center;padding:40px;">이메일 콘텐츠가 없습니다.</p>');
      }
    } catch {
      setPreviewHtml('<p style="color:#999;text-align:center;padding:40px;">미리보기를 불러올 수 없습니다.</p>');
    }
    setPreviewLoading(false);
  };

  const handlePause = async () => {
    try {
      await campaignsApi.pause(id);
      message.success('캠페인이 일시정지되었습니다.');
      fetchData();
    } catch (error) {
      message.error('일시정지에 실패했습니다.');
    }
  };

  const getStatusTag = (status) => {
    const config = {
      draft: { color: 'default', text: '초안' },
      scheduled: { color: 'blue', text: '예약됨' },
      running: { color: 'green', text: '진행' },
      paused: { color: 'orange', text: '일시정지' },
      completed: { color: 'purple', text: '완료' },
      cancelled: { color: 'red', text: '취소됨' }
    };
    const { color, text } = config[status] || { color: 'default', text: status };
    return <Tag color={color}>{text}</Tag>;
  };

  const recipientColumns = [
    { title: '이름', dataIndex: 'name', key: 'name', width: 150 },
    { title: '이메일', dataIndex: 'email', key: 'email', width: 250 },
    {
      title: '상태', dataIndex: 'status', key: 'status', width: 80,
      render: (status, record) => {
        if (record.unsubscribed_at) return <Tag color="red">수신거부</Tag>;
        return <Tag color={status?.toLowerCase() === 'sent' ? 'green' : 'orange'}>{status}</Tag>;
      }
    },
    {
      title: '발송', dataIndex: 'sent_at', key: 'sent_at', width: 140,
      render: (val) => val ? dayjs(val).add(9, 'hour').format('MM-DD HH:mm:ss') : '-'
    },
    {
      title: '오픈', key: 'opened', width: 140,
      render: (_, record) => record.opened_at ? (
        <Space><EyeOutlined style={{ color: '#52c41a' }} />{dayjs(record.opened_at).add(9, 'hour').format('MM-DD HH:mm:ss')}</Space>
      ) : '-'
    },
    {
      title: '클릭', key: 'clicked', width: 160,
      render: (_, record) => record.clicked_at ? (
        <Space>
          <LinkOutlined style={{ color: '#1890ff' }} />
          {dayjs(record.clicked_at).add(9, 'hour').format('MM-DD HH:mm:ss')}
          {record.click_count > 1 && <Tag>x{record.click_count}</Tag>}
        </Space>
      ) : '-'
    },
    {
      title: '수신거부', key: 'unsubscribed', width: 140,
      render: (_, record) => record.unsubscribed_at ? (
        <Space><StopOutlined style={{ color: '#ff4d4f' }} />{dayjs(record.unsubscribed_at).add(9, 'hour').format('MM-DD HH:mm:ss')}</Space>
      ) : '-'
    },
  ];

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: 100 }}><Spin size="large" /></div>;
  }

  if (!campaign) {
    return <div>캠페인을 찾을 수 없습니다.</div>;
  }

  const totalSent = recipients?.recipients?.filter(r => r.sent_at).length || 0;
  const totalOpened = recipients?.opened || 0;
  const totalClicked = recipients?.clicked || 0;
  const totalUnsubscribed = recipients?.unsubscribed || 0;
  const openRate = totalSent > 0 ? (totalOpened / totalSent * 100).toFixed(1) : 0;
  const clickRate = totalOpened > 0 ? (totalClicked / totalOpened * 100).toFixed(1) : 0;

  const isAiCampaign = campaign.name?.startsWith('[AI]');

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/campaigns')}>목록</Button>
          <h2 style={{ margin: 0 }}>{campaign.name}</h2>
          {getStatusTag(campaign.status)}
          {isAiCampaign && <Tag color="purple">🤖 AI 생성</Tag>}
        </Space>
        <Space>
          {campaign.status === 'running' && (
            <Button icon={<PauseCircleOutlined />} onClick={handlePause}>일시정지</Button>
          )}
        </Space>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={12} md={4}>
          <Card size="small"><Statistic title="대상" value={campaign.total_recipients || recipients?.total || 0} prefix={<MailOutlined />} /></Card>
        </Col>
        <Col xs={12} sm={12} md={4}>
          <Card size="small"><Statistic title="발송" value={totalSent} prefix={<CheckCircleOutlined />} /></Card>
        </Col>
        <Col xs={12} sm={12} md={4}>
          <Card size="small"><Statistic title="오픈률" value={openRate} suffix="%" prefix={<EyeOutlined />} valueStyle={{ color: '#52c41a' }} /></Card>
        </Col>
        <Col xs={12} sm={12} md={4}>
          <Card size="small"><Statistic title="클릭률" value={clickRate} suffix="%" prefix={<LinkOutlined />} valueStyle={{ color: '#1890ff' }} /></Card>
        </Col>
        <Col xs={12} sm={12} md={4}>
          <Card size="small"><Statistic title="수신거부" value={totalUnsubscribed} prefix={<StopOutlined />} valueStyle={{ color: totalUnsubscribed > 0 ? '#ff4d4f' : '#999' }} /></Card>
        </Col>
      </Row>

      <Card>
        <Tabs activeKey={activeTab} onChange={(key) => { setActiveTab(key); if (key === 'preview') loadPreview(); }} items={[
          {
            key: 'overview',
            label: '개요',
            children: (
              <div>
                <h4>캠페인 정보</h4>
                <p><strong>생성일:</strong> {dayjs(campaign.created_at).add(9, 'hour').format('YYYY-MM-DD HH:mm')}</p>
                {campaign.started_at && <p><strong>시작:</strong> {dayjs(campaign.started_at).add(9, 'hour').format('YYYY-MM-DD HH:mm')}</p>}
                {campaign.completed_at && <p><strong>완료:</strong> {dayjs(campaign.completed_at).add(9, 'hour').format('YYYY-MM-DD HH:mm')}</p>}
                {campaign.description && <p><strong>설명:</strong> {campaign.description}</p>}
                <h4 style={{ marginTop: 20 }}>통계 요약</h4>
                <p>발송: {totalSent}명 / 오픈: {totalOpened}명 ({openRate}%) / 클릭: {totalClicked}명 ({clickRate}%) / 수신거부: {totalUnsubscribed}명</p>
              </div>
            )
          },
          {
            key: 'preview',
            label: <span><EyeOutlined /> 이메일 미리보기</span>,
            children: (
              <div>
                {previewLoading ? (
                  <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /><p style={{ marginTop: 12, color: '#888' }}>이메일 미리보기 로딩 중...</p></div>
                ) : previewHtml ? (
                  <div>
                    {previewSubject && (
                      <div style={{ background: '#f6f8fa', borderRadius: 8, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Tag color="blue">제목</Tag>
                        <span style={{ fontWeight: 600, fontSize: 14 }}>{previewSubject}</span>
                      </div>
                    )}
                    <div style={{ border: '1px solid #e8e8e8', borderRadius: 8, overflow: 'hidden', background: '#f0f2f5' }}>
                      {/* 이메일 클라이언트 바 */}
                      <div style={{ background: '#fff', borderBottom: '1px solid #e8e8e8', padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#ff5f57' }} />
                        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#febc2e' }} />
                        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#28c840' }} />
                        <span style={{ marginLeft: 12, fontSize: 12, color: '#999' }}>이메일 미리보기</span>
                      </div>
                      {/* 이메일 본문 */}
                      <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
                        <div style={{ maxWidth: 620, width: '100%', background: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', borderRadius: 4 }}>
                          <iframe
                            srcDoc={previewHtml}
                            style={{ width: '100%', minHeight: 600, border: 'none', borderRadius: 4 }}
                            title="이메일 미리보기"
                            sandbox="allow-same-origin"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>
                    <MailOutlined style={{ fontSize: 48, marginBottom: 12 }} />
                    <p>이메일 콘텐츠가 없습니다.</p>
                  </div>
                )}
              </div>
            )
          },
          {
            key: 'recipients',
            label: `수신자 (${recipients?.total || 0}명)`,
            children: (
              <div>
                <Row gutter={16} style={{ marginBottom: 16 }}>
                  <Col><Tag color="green">오픈: {totalOpened}명</Tag></Col>
                  <Col><Tag color="blue">클릭: {totalClicked}명</Tag></Col>
                  <Col><Tag color="red">수신거부: {totalUnsubscribed}명</Tag></Col>
                </Row>
                <Table
                  columns={recipientColumns}
                  dataSource={recipients?.recipients || []}
                  rowKey="id"
                  size="small"
                  pagination={{ pageSize: 20 }}
                  scroll={{ x: 1100 }}
                />
              </div>
            )
          }
        ]} />
      </Card>
    </div>
  );
};

export default CampaignDetail;
